import { useEffect, useRef } from "react";
import { useAtomValue } from "jotai";
import { logger } from "@artis/logger";
import { useAuth0 } from "@auth0/auth0-react";
import { gridLoadingAtom } from "../../stateAtoms";
import { hideInitialLoader, showInitialLoader } from "./initial-loader";

declare global {
  interface Window {
    __LOADER_START_TIME__?: number;
  }
}

export default function Loading(_props: { showLogo?: boolean; syncing?: boolean; workerLoading?: boolean }) {
  return null;
}

const MSG_DELAY = 10000;
const TOO_LONG_DELAY = 120000;

export function AppLoading() {
  const { logout } = useAuth0();
  const isLoading = useAtomValue(gridLoadingAtom);
  const refs = useRef<{ first?: HTMLElement; second?: HTMLElement; timers: number[] }>({ timers: [] });

  useEffect(() => {
    const container = document.getElementById("initial-loader-messages");
    if (!container) return;

    const first = document.createElement("p");
    first.id = "il-msg-first";
    first.textContent = "Please allow up to 2 minutes as we load your access and data packages.";

    const second = document.createElement("div");
    second.id = "il-msg-second";
    second.innerHTML =
      "<p>This is taking longer than it should. Please refresh, log out and log in again, or contact Support.</p><button>Log out</button>";
    second.querySelector("button")?.addEventListener("click", function () {
      this.textContent = "Logging out...";
      this.disabled = true;
      logout({ logoutParams: { returnTo: window.location.origin } });
    });

    container.append(first, second);
    refs.current.first = first;
    refs.current.second = second;

    return () => {
      first.remove();
      second.remove();
    };
  }, [logout]);

  useEffect(() => {
    const loader = document.getElementById("initial-loader");
    const { first, second, timers } = refs.current;
    if (!loader) return;

    const clearTimers = () => {
      timers.forEach(clearTimeout);
      timers.length = 0;
    };

    if (isLoading) {
      showInitialLoader();
      const elapsed = Date.now() - (window.__LOADER_START_TIME__ ?? Date.now());

      timers.push(
        window.setTimeout(() => first?.classList.add("il-visible"), Math.max(0, MSG_DELAY - elapsed)),
        window.setTimeout(
          () => {
            first?.classList.remove("il-visible");
            timers.push(window.setTimeout(() => second?.classList.add("il-visible"), 500));
            logger.warn("Loading taking too long");
          },
          Math.max(0, TOO_LONG_DELAY - elapsed),
        ),
      );
    } else {
      hideInitialLoader();
      clearTimers();
      first?.classList.remove("il-visible");
      second?.classList.remove("il-visible");
    }

    return clearTimers;
  }, [isLoading]);

  return null;
}
